import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-4xl font-black text-white">Terms of Service</h1>
          <p className="text-slate-400 mt-2">Last updated: March 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-600 mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", 
              or "your") and Gantt Account Management (Pty) Ltd ("Company", "we", "our", or "us") governing your 
              access to and use of the Gantt construction management software platform and related services 
              (collectively, the "Service").
            </p>
            <p className="text-slate-600">
              By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to 
              these Terms, you may not access or use the Service.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 mb-4">
              Gantt provides a cloud-based construction project management platform that includes:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Delay event tracking and management</li>
              <li>AI-powered notice and claim generation compliant with JBCC standards</li>
              <li>Request for Information (RFI) management</li>
              <li>Site instruction tracking</li>
              <li>Variation order management</li>
              <li>Meeting minutes recording and action tracking</li>
              <li>Information Required Schedule (IRS) generation</li>
              <li>Shop drawing tracking</li>
              <li>Procurement schedule management</li>
              <li>Document generation and email distribution</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. User Accounts</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mb-3">3.1 Account Registration</h3>
            <p className="text-slate-600 mb-4">
              To access the Service, you must create an account by providing accurate, complete, and current 
              information. You are responsible for maintaining the confidentiality of your account credentials 
              and for all activities that occur under your account.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">3.2 Account Security</h3>
            <p className="text-slate-600 mb-4">
              You must immediately notify us of any unauthorised use of your account or any other breach of 
              security. We will not be liable for any loss or damage arising from your failure to protect 
              your account credentials.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">3.3 User Roles</h3>
            <p className="text-slate-600">
              The Service supports multiple user roles with varying access permissions. Account administrators 
              are responsible for managing user access and permissions within their organisation.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Subscription and Payment</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mb-3">4.1 Subscription Plans</h3>
            <p className="text-slate-600 mb-4">
              Access to the Service requires a paid subscription. Subscription fees are based on project value 
              tiers and are billed monthly in advance. Current pricing is available on our website.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">4.2 Payment Terms</h3>
            <p className="text-slate-600 mb-4">
              All fees are quoted in South African Rand (ZAR) and are exclusive of VAT unless otherwise stated. 
              Payment is due upon invoice. Late payments may result in suspension of access to the Service.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">4.3 Cancellation</h3>
            <p className="text-slate-600">
              You may cancel your subscription at any time with 30 days written notice. No refunds will be 
              provided for partial months. Upon cancellation, you will retain access until the end of your 
              current billing period.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Acceptable Use</h2>
            <p className="text-slate-600 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
              <li>Attempt to gain unauthorised access to any part of the Service or other systems</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Upload or transmit viruses, malware, or other harmful code</li>
              <li>Use the Service to infringe upon the rights of others</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
              <li>Share your account credentials with unauthorised parties</li>
              <li>Use automated systems to access the Service without our permission</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mb-3">6.1 Our Intellectual Property</h3>
            <p className="text-slate-600 mb-4">
              The Service, including all software, algorithms, designs, text, graphics, and other content, 
              is owned by or licensed to us and is protected by copyright, trademark, and other intellectual 
              property laws. You may not copy, modify, distribute, or create derivative works without our 
              express written permission.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">6.2 Your Content</h3>
            <p className="text-slate-600">
              You retain ownership of all data, documents, and content you upload to the Service ("Your Content"). 
              By uploading Your Content, you grant us a limited licence to store, process, and display Your 
              Content solely for the purpose of providing the Service to you.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. AI-Generated Content</h2>
            <p className="text-slate-600 mb-4">
              The Service uses artificial intelligence to generate notices, claims, and other documents based 
              on information you provide. You acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>AI-generated content is provided as a starting point and should be reviewed before use</li>
              <li>You are responsible for verifying the accuracy and appropriateness of all generated content</li>
              <li>We do not guarantee that AI-generated content will meet specific legal requirements</li>
              <li>You should seek independent legal advice for complex contractual matters</li>
              <li>We are not liable for any consequences arising from the use of AI-generated content</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. JBCC Compliance</h2>
            <p className="text-slate-600">
              While the Service is designed to assist with generating JBCC-compliant documentation, we do not 
              guarantee compliance with any specific version of the JBCC Principal Building Agreement or any 
              other contract form. Users are responsible for ensuring all documentation meets their specific 
              contractual requirements and should consult with qualified professionals where necessary.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-slate-600 mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS 
              OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A 
              PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-slate-600">
              We do not warrant that the Service will be uninterrupted, error-free, or secure, or that any 
              defects will be corrected. We do not warrant the accuracy or completeness of any information 
              provided through the Service.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, 
              OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE.
            </p>
            <p className="text-slate-600">
              Our total liability for any claims arising from these Terms or your use of the Service shall not 
              exceed the amount you paid to us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Indemnification</h2>
            <p className="text-slate-600">
              You agree to indemnify, defend, and hold harmless Gantt Account Management (Pty) Ltd and its 
              officers, directors, employees, and agents from and against any claims, damages, losses, 
              liabilities, costs, and expenses (including reasonable attorneys' fees) arising from your use 
              of the Service, your violation of these Terms, or your violation of any rights of a third party.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Data Protection</h2>
            <p className="text-slate-600">
              Our collection and use of personal information is governed by our Privacy Policy, which is 
              incorporated into these Terms by reference. By using the Service, you consent to our data 
              practices as described in the Privacy Policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Termination</h2>
            <p className="text-slate-600 mb-4">
              We may suspend or terminate your access to the Service at any time, with or without cause, and 
              with or without notice. Upon termination:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Your right to use the Service will immediately cease</li>
              <li>You may request a copy of Your Content within 30 days of termination</li>
              <li>We may delete Your Content after 90 days from termination</li>
              <li>Provisions that by their nature should survive termination will remain in effect</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Governing Law and Jurisdiction</h2>
            <p className="text-slate-600">
              These Terms shall be governed by and construed in accordance with the laws of the Republic of 
              South Africa. Any disputes arising from these Terms or your use of the Service shall be subject 
              to the exclusive jurisdiction of the courts of South Africa, sitting in Johannesburg.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Changes to Terms</h2>
            <p className="text-slate-600">
              We reserve the right to modify these Terms at any time. We will provide notice of material 
              changes by posting the updated Terms on our website and updating the "Last updated" date. Your 
              continued use of the Service after any changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">16. Contact Information</h2>
            <p className="text-slate-600 mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="bg-slate-100 p-6 rounded-lg">
              <p className="text-slate-700"><strong>Gantt Account Management (Pty) Ltd</strong></p>
              <p className="text-slate-600">Email: info@gantt-acc.co.za</p>
              <p className="text-slate-600">Phone: 073 049 1360</p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Gantt Account Management. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
